export const DEFAULT_RSVP_DEADLINE_OFFSET_MINUTES = 24 * 60;
export const DEFAULT_RSVP_REMINDER_OFFSET_MINUTES = 24 * 60;
export const MIN_RSVP_REMINDER_OFFSET_MINUTES = 30;
export const MAX_RSVP_REMINDER_COUNT = 3;
export const DEFAULT_RSVP_REMINDER_OFFSETS = [24 * 60, 6 * 60, 30];

function cleanString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

export function normalizePositiveMinutes(value, fallback = DEFAULT_RSVP_DEADLINE_OFFSET_MINUTES) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 1) return fallback;
  return Math.max(1, Math.floor(n));
}

export function normalizeRsvpReminderOffsets(reminder = {}, fallback = [DEFAULT_RSVP_REMINDER_OFFSET_MINUTES]) {
  const rawValues = Array.isArray(reminder)
    ? reminder
    : Array.isArray(reminder?.offsetMinutesBeforeDeadlineList)
      ? reminder.offsetMinutesBeforeDeadlineList
      : [reminder?.offsetMinutesBeforeDeadline ?? fallback?.[0] ?? DEFAULT_RSVP_REMINDER_OFFSET_MINUTES];
  const unique = Array.from(new Set(rawValues
    .map((value) => Math.max(MIN_RSVP_REMINDER_OFFSET_MINUTES, normalizePositiveMinutes(
      value,
      fallback?.[0] ?? DEFAULT_RSVP_REMINDER_OFFSET_MINUTES
    )))
    .filter((value) => Number.isFinite(value))));
  const sorted = unique.sort((a, b) => b - a).slice(0, MAX_RSVP_REMINDER_COUNT);
  return sorted.length > 0 ? sorted : [DEFAULT_RSVP_REMINDER_OFFSET_MINUTES];
}

function toMillis(value) {
  if (!value) return null;
  if (typeof value.toMillis === 'function') {
    const millis = value.toMillis();
    return Number.isFinite(millis) ? millis : null;
  }
  if (typeof value.toDate === 'function') {
    const date = value.toDate();
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date.getTime() : null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.getTime();
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
}

function parseLocalDateTime(dateValue, timeValue = '00:00') {
  const date = cleanString(dateValue);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const time = cleanString(timeValue) || '00:00';
  const normalizedTime = /^\d{2}:\d{2}$/.test(time) ? time : '00:00';
  const parsed = new Date(`${date}T${normalizedTime}`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function getEventStartDate(eventData = {}, occurrenceId = '') {
  const occurrence = cleanString(occurrenceId || eventData.occurrenceId);
  if (occurrence) {
    return parseLocalDateTime(
      occurrence,
      eventData.time || eventData.recurrence?.time || '00:00'
    );
  }
  const directStart = toMillis(eventData.start);
  if (directStart) return new Date(directStart);
  return parseLocalDateTime(eventData.date, eventData.time || '00:00');
}

export function isRecurringRsvpDeadlineContext(eventData = {}, occurrenceId = '') {
  return Boolean(
    cleanString(occurrenceId || eventData.occurrenceId)
    || cleanString(eventData.originalEventId)
    || eventData.scope === 'occurrence'
    || eventData.type === 'recurring'
    || (Array.isArray(eventData.recurrence?.byDay) && eventData.recurrence.byDay.length > 0)
  );
}

export function getEffectiveRsvpDeadline(eventData = {}, { occurrenceId = '', now = new Date() } = {}) {
  const deadline = eventData.rsvpDeadline || {};
  if (deadline.enabled !== true) return null;

  const isRecurringContext = isRecurringRsvpDeadlineContext(eventData, occurrenceId);
  if (isRecurringContext && !deadline.deadlineAt) {
    const eventStart = getEventStartDate(eventData, occurrenceId);
    if (!eventStart) return null;
    const offset = normalizePositiveMinutes(
      deadline.offsetMinutesBeforeStart,
      DEFAULT_RSVP_DEADLINE_OFFSET_MINUTES
    );
    return new Date(eventStart.getTime() - offset * 60 * 1000);
  }

  const deadlineMillis = toMillis(deadline.deadlineAt);
  if (!deadlineMillis) return null;
  return new Date(deadlineMillis);
}

export function isRsvpDeadlinePassed(eventData = {}, options = {}) {
  const now = options.now instanceof Date ? options.now : new Date(options.now || Date.now());
  const deadline = getEffectiveRsvpDeadline(eventData, { ...options, now });
  return Boolean(deadline && now.getTime() >= deadline.getTime());
}

export function getRsvpReminderSendAt(deadline, offsetMinutesBeforeDeadline = DEFAULT_RSVP_REMINDER_OFFSET_MINUTES) {
  const deadlineMillis = toMillis(deadline);
  if (!deadlineMillis) return null;
  const offset = normalizePositiveMinutes(
    offsetMinutesBeforeDeadline,
    DEFAULT_RSVP_REMINDER_OFFSET_MINUTES
  );
  return new Date(deadlineMillis - offset * 60 * 1000);
}

export function formatRsvpDeadline(deadline, { includePrefix = true } = {}) {
  if (!(deadline instanceof Date) || Number.isNaN(deadline.getTime())) return '';
  const text = deadline.toLocaleString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
  return includePrefix ? `RSVP deadline: ${text}` : text;
}
